/**
 * WebSocket client (FOUNDATION — BUILD-PLAN src/lib/ws.ts).
 *
 * Implements the realtime-protocol.md client contract:
 *  1. POST /api/v1/realtime/ticket  (bearer; 10/min bucket)
 *  2. open wss://<host>/ws and send `auth {ticket}` as the FIRST frame (<5 s)
 *  3. server answers `auth.ok` — or closes 4401
 *  4. heartbeat: server pings every 25 s → we answer `pong`; we also send a
 *     keepalive `pong` every 25 s so the socket is never idle (ARCH §3), and
 *     a liveness watchdog reconnects if nothing arrives for 60 s
 *  5. close 4403 = auth_version revoked → we re-ticket (the api layer's
 *     refresh decides whether the session is still alive)
 *
 * Commands (`wsCommand`) carry a client_event_id and resolve on their `ack`
 * (same idempotency semantics as the REST twin — WS is a transport, not a
 * second code path). Pushes are fire-and-forget hints: HANDLERS MUST RE-RENDER
 * SERVER STATE, NEVER COMPUTE AN OUTCOME (SM §0 — the server is the referee).
 *
 * Vertical usage:
 *   const off = ws.on("msg.new", ({ match_id, message }) => { ... });
 *   await wsCommand("msg.send", { match_id, kind: "text", body });
 *   ws.ensureConnected();     // idempotent; call when entering a realtime screen
 */
import { api } from "./api.js";
import { sessionStore, wsStore } from "./store.js";

// ---------------------------------------------------------------------------
// server push catalog (realtime-protocol §4) — payload types for subscribers.
// Verticals get compile-time payloads for the frames they own; unknown/new
// types remain subscribable via the untyped overload.
// ---------------------------------------------------------------------------
export interface UserRefWire {
  id?: string;
  display_name?: string;
  avatar_media_id?: string;
  verified?: boolean;
  purged?: boolean;
}

export interface ServerPushMap {
  // §4.1 deck, match & swipe resolution
  "match.created": { match: unknown; reveal: { copy_key: string; crush_context?: unknown } };
  "match.expiring": { match_id: string; due_at: string };
  "match.expired": { match_id: string };
  "match.extended": { match_id: string; due_at: string; extensions_used: number };
  "match.unmatched": { match_id: string };
  "rib.offered": { match_id: string; expires_at: string };
  "rib.reopened": { match: unknown };
  "gg.completed": { match_id: string; honor_points: number };
  "crush.received": Record<string, never>;
  // §4.2 SR / shield / cooldown (server-authoritative resolutions)
  "sr.landed": { sr_id: string; cooldown_expires_at: string };
  "cooldown.started": { source: "hidden"; expires_at: string };
  "cooldown.ended": Record<string, never>;
  "sr.result": { state: "accepted" };
  "sr.reversed": { copy_key: string };
  "shield.blocked": { sr_id: string; attacker: UserRefWire; blocked_at: string };
  "shield.fizzled": Record<string, never>;
  "shield.armed": { expires_at: string };
  // §4.3 killstreak & arsenal
  "streak.updated": { count: number; tier_reached?: number };
  "streak.tier": { tier: 3 | 5 | 7 | 10 | "beyond"; grant?: { item: string; lost: boolean } };
  "streak.broken": Record<string, never>;
  "inventory.updated": { balances: Record<string, number>; takeover_token?: unknown };
  "berserker.started": { expires_at: string };
  "berserker.ended": Record<string, never>;
  "boost.started": { expires_at: string };
  "boost.ended": Record<string, never>;
  "pity.granted": { item: "boost" };
  // §4.4 chat
  "msg.new": { match_id: string; message: { [k: string]: unknown; seq?: number } };
  "msg.unsent": { match_id: string; message_id: string };
  "msg.reaction": { match_id: string; message_id: string; user_id: string; emoji: string | null };
  "receipt.read": { match_id: string; reader_id: string; seq: number };
  typing: { match_id: string; user_id: string; on: boolean };
  "presence.update": { user_id: string; bucket: "online" | "today" | "this_week" | "none" };
  // §4.5 calls
  "call.incoming": { call_id: string; match_id: string; kind: "voice" | "video"; caller: UserRefWire };
  "call.state": {
    call_id: string;
    state: "connecting" | "active" | "ended";
    reason?: string;
  };
  "call.sdp": { call_id: string; desc: { type: "offer" | "answer"; sdp: string } };
  "call.ice": { call_id: string; candidate: unknown };
  "call.missed": { call_id: string; match_id: string };
  // §4.6 roulette
  "roulette.queued": { position_hint?: number };
  "roulette.paired": {
    session_id: string;
    partner: { display_name: string; age: number; verified: boolean };
    mode: string;
  };
  "roulette.live": { session_id: string };
  "roulette.msg": { session_id: string; from: "partner" | "me"; body: string; folded?: boolean };
  "roulette.typing": { session_id: string; on: boolean };
  "roulette.sdp": { session_id: string; desc?: unknown };
  "roulette.ice": { session_id: string; candidate?: unknown };
  "roulette.ended": {
    session_id: string;
    reason_class: "partner_left" | "you_left" | "vibe_match" | "mod";
    end_card?: {
      session_id: string;
      partner_display_name: string;
      report_available: boolean;
      block_available: boolean;
    };
  };
  "roulette.gate": { until: string; reason: "skip_velocity" | "none"; recent_partners: unknown[] };
  "roulette.queue_timeout": Record<string, never>;
  // §4.7 takeover
  "takeover.offer": { takeover: unknown };
  "takeover.canceled": { takeover_id: string };
  "takeover.accepted": { takeover_id: string };
  "takeover.declined": { takeover_id: string };
  "takeover.expired": { takeover_id: string };
  "takeover.updated": { takeover_id: string; revision: number };
  "takeover.killed": { takeover_id: string; by: "owner" | "mod" | "account_event" };
  // §4.8 account, moderation, commerce
  premium_changed: { entitled: boolean; status: string; current_period_end?: string };
  "restriction.applied": { surface: string; until?: string };
  "restriction.lifted": { surface: string };
  "moderation.notice": {
    copy_key: string;
    media_id?: string;
    strike?: { n: number; ladder_step: string };
  };
  "verification.result": { status: "verified" | "rejected" | "revoked"; reason?: string };
  "export.ready": { export_id: string; expires_at: string };
  "account.notice": { copy_key: string };
  // lifecycle frames
  "auth.ok": { user_id: string; resume: { server_time: string } };
  ping: Record<string, never>;
}

export interface ServerFrame {
  t: string;
  id?: string;
  ts?: string;
  p?: unknown;
}

interface AckPayload {
  for: string;
  ok: boolean;
  data?: unknown;
  error?: { code: string; message: string };
}

export class WsCommandError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "WsCommandError";
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// client
// ---------------------------------------------------------------------------
const KEEPALIVE_MS = 25_000; // ARCH §3: the socket is never idle
const WATCHDOG_MS = 60_000; // 2 missed server pings + slack
const ACK_TIMEOUT_MS = 15_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

type Handler = (payload: never, frame: ServerFrame) => void;

class HumbleWs {
  private socket: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private anyHandlers = new Set<(frame: ServerFrame) => void>();
  private pendingAcks = new Map<
    string,
    { resolve: (data: unknown) => void; reject: (err: Error) => void; timer: number }
  >();
  private wantOpen = false;
  private attempts = 0;
  private keepalive: number | undefined;
  private watchdog: number | undefined;
  private reconnectTimer: number | undefined;

  /** Idempotent: connects if authenticated and not already open/connecting. */
  ensureConnected(): void {
    this.wantOpen = true;
    if (this.socket || sessionStore.get().status !== "authed") return;
    void this.connect();
  }

  /** Deliberate close (logout, grace) — no auto-reconnect afterwards. */
  disconnect(): void {
    this.wantOpen = false;
    this.teardown();
    wsStore.set({ status: "idle" });
  }

  /** Subscribe to a typed server push. Returns the unsubscribe function. */
  on<K extends keyof ServerPushMap & string>(
    type: K,
    handler: (payload: ServerPushMap[K], frame: ServerFrame) => void,
  ): () => void;
  on(type: string, handler: (payload: unknown, frame: ServerFrame) => void): () => void;
  on(type: string, handler: (payload: never, frame: ServerFrame) => void): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler);
    return () => {
      set.delete(handler as Handler);
    };
  }

  /** Subscribe to every frame (debug / gallery). */
  onAny(handler: (frame: ServerFrame) => void): () => void {
    this.anyHandlers.add(handler);
    return () => {
      this.anyHandlers.delete(handler);
    };
  }

  /**
   * Send a client command and await its ack (realtime-protocol §1). The
   * frame id IS the client_event_id (SM §0) — pass one to make a retry
   * idempotent, otherwise a fresh UUID is minted.
   */
  command<TData = unknown>(t: string, p: unknown, clientEventId?: string): Promise<TData> {
    const id = clientEventId ?? crypto.randomUUID();
    return new Promise<TData>((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new WsCommandError("NOT_CONNECTED", "socket is not open"));
        return;
      }
      const timer = window.setTimeout(() => {
        this.pendingAcks.delete(id);
        reject(new WsCommandError("ACK_TIMEOUT", `no ack for ${t} within ${ACK_TIMEOUT_MS}ms`));
      }, ACK_TIMEOUT_MS);
      this.pendingAcks.set(id, {
        resolve: resolve as (data: unknown) => void,
        reject,
        timer,
      });
      this.send({ t, id, p });
    });
  }

  // -- internals -------------------------------------------------------------
  private send(frame: { t: string; id?: string; p?: unknown }): void {
    this.socket?.send(JSON.stringify(frame));
  }

  private async connect(): Promise<void> {
    wsStore.set({ status: "connecting" });
    let ticket: string;
    try {
      const res = await api.post("/realtime/ticket");
      if (!res?.ticket) throw new Error("no ticket in response");
      ticket = res.ticket;
    } catch {
      this.scheduleReconnect();
      return;
    }
    if (!this.wantOpen) return;

    const proto = location.protocol === "https:" ? "wss" : "ws";
    const socket = new WebSocket(`${proto}://${location.host}/ws`);
    this.socket = socket;

    socket.onopen = () => {
      // first frame within 5 s: auth {ticket}
      socket.send(JSON.stringify({ t: "auth", id: crypto.randomUUID(), p: { ticket } }));
    };
    socket.onmessage = (ev) => this.handleMessage(ev);
    socket.onclose = (ev) => {
      this.teardownTimers();
      this.socket = null;
      this.failPending(new WsCommandError("SOCKET_CLOSED", `socket closed (${ev.code})`));
      wsStore.set({ status: "closed" });
      if (this.wantOpen) this.scheduleReconnect();
    };
    socket.onerror = () => {
      // onclose follows; nothing to do here
    };
  }

  private handleMessage(ev: MessageEvent): void {
    let frame: ServerFrame;
    try {
      frame = JSON.parse(String(ev.data)) as ServerFrame;
    } catch {
      return; // malformed server frame — ignore, watchdog covers a dead peer
    }
    this.armWatchdog();

    if (frame.t === "auth.ok") {
      this.attempts = 0;
      wsStore.set({ status: "open" });
      this.armKeepalive();
    } else if (frame.t === "ping") {
      this.send({ t: "pong", p: {} });
    } else if (frame.t === "ack") {
      const p = frame.p as AckPayload | undefined;
      if (p?.for) {
        const pending = this.pendingAcks.get(p.for);
        if (pending) {
          this.pendingAcks.delete(p.for);
          window.clearTimeout(pending.timer);
          if (p.ok) pending.resolve(p.data);
          else
            pending.reject(
              new WsCommandError(p.error?.code ?? "UNKNOWN", p.error?.message ?? "command failed"),
            );
        }
      }
    }

    const set = this.handlers.get(frame.t);
    if (set) {
      for (const h of set) (h as (payload: unknown, f: ServerFrame) => void)(frame.p, frame);
    }
    for (const h of this.anyHandlers) h(frame);
  }

  private armKeepalive(): void {
    window.clearInterval(this.keepalive);
    this.keepalive = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) this.send({ t: "pong", p: {} });
    }, KEEPALIVE_MS);
  }

  private armWatchdog(): void {
    window.clearTimeout(this.watchdog);
    this.watchdog = window.setTimeout(() => {
      // nothing heard for 60 s — the peer is gone; force a reconnect cycle
      this.socket?.close(4000, "liveness watchdog");
    }, WATCHDOG_MS);
  }

  private scheduleReconnect(): void {
    if (!this.wantOpen || this.reconnectTimer !== undefined) return;
    const backoff = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempts);
    const jitter = backoff * (0.7 + Math.random() * 0.6);
    this.attempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.wantOpen && !this.socket && sessionStore.get().status === "authed") {
        void this.connect();
      }
    }, jitter);
  }

  private failPending(err: Error): void {
    for (const [, pending] of this.pendingAcks) {
      window.clearTimeout(pending.timer);
      pending.reject(err);
    }
    this.pendingAcks.clear();
  }

  private teardownTimers(): void {
    window.clearInterval(this.keepalive);
    window.clearTimeout(this.watchdog);
    this.keepalive = undefined;
    this.watchdog = undefined;
  }

  private teardown(): void {
    this.teardownTimers();
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    const s = this.socket;
    this.socket = null;
    s?.close(1000, "client disconnect");
    this.failPending(new WsCommandError("SOCKET_CLOSED", "client disconnect"));
  }
}

/** The one app socket. Reconnect/backfill rule: on reconnect, screens re-fetch
 *  their REST recovery plane (e.g. GET /matches/:id/messages?after_seq=) —
 *  WS is a notification plane only (realtime-protocol §2.6). */
export const ws = new HumbleWs();

export function wsCommand<TData = unknown>(
  t: string,
  p: unknown,
  clientEventId?: string,
): Promise<TData> {
  return ws.command<TData>(t, p, clientEventId);
}
